// Neutral search/category helpers — relocated from
// custom/tealium/tealium-page-processors.js during the Tealium removal.
// These feed the SURVIVING analytics paths (GA4 view_search_results +
// the onlineServices most_represented_category / page_view), so they are
// NOT Tealium code and must not be deleted with the Tealium module.
// They read only globals (sessionStorage / document / window.LG_PAGE_DATA).

import createLogger from '../../common/utils/logger';

const log = createLogger('SearchUtils');

export function getLatestSearchFromCache(combineUnfilteredAndFiltered = false) {
    const CACHE_KEY = 'ss-networkcache';
    const SEARCH_API_PREFIX = '/api/search/search';

    try {
        const rawCacheData = sessionStorage.getItem(CACHE_KEY);
        if (!rawCacheData) {
            log.debug("Session storage cache ('ss-networkcache') is empty.");
            return null;
        }

        const allCacheEntries = JSON.parse(rawCacheData);
        const facetsSelected = document.querySelectorAll("#facetedSearch-content--product-type > li > a.is-selected");

        let searchCacheEntries = Object.entries(allCacheEntries)
            .filter(([key]) => key.startsWith(SEARCH_API_PREFIX))
            .map(([key, value]) => ({ key, value }));

        if (searchCacheEntries.length === 0) {
            log.debug("No search results found in the cache.");
            return null;
        }

        const filtersApplied = facetsSelected && facetsSelected.length > 0;

        if (combineUnfilteredAndFiltered) {
            // Merge results from all cache entries
            const mergedResultsMap = new Map();
            let latestEntry = null;

            for (const entry of searchCacheEntries) {
                if (entry?.value?.value?.results) {
                    for (const result of entry.value.value.results) {
                        if (!mergedResultsMap.has(result.sku)) {
                            mergedResultsMap.set(result.sku, result);
                        }
                    }
                }

                if (!latestEntry || entry.value.expires > latestEntry.value.expires) {
                    latestEntry = entry;
                }
            }

            const mergedResults = Array.from(mergedResultsMap.values());
            const sourceValue = latestEntry?.value?.value ?? {};

            return {
                value: {
                    ...sourceValue,
                    results: mergedResults,
                    pagination: {
                        ...sourceValue.pagination,
                        totalResults: mergedResults.length,
                        begin: 1,
                        end: Math.min(mergedResults.length, sourceValue.pagination?.perPage || 12),
                        currentPage: 1,
                        totalPages: Math.ceil(mergedResults.length / (sourceValue.pagination?.perPage || 12)),
                        previousPage: 0,
                        nextPage: mergedResults.length > (sourceValue.pagination?.perPage || 12) ? 2 : 0,
                        perPage: sourceValue.pagination?.perPage || 12,
                        defaultPerPage: sourceValue.pagination?.defaultPerPage || 12
                    },
                    filterSummary: [], // because results are combined from all filters
                    breadcrumbs: []    // safe default
                },
                expires: latestEntry?.value?.expires || Date.now(),
                purgeable: true
            };
        } else {
            // Default behavior: return latest entry filtered by filter presence
            if (!filtersApplied) {
                searchCacheEntries = searchCacheEntries.filter(entry => {
                    const summary = entry.value?.value?.filterSummary;
                    return !summary || summary.length === 0;
                });
            }

            if (searchCacheEntries.length === 0) {
                log.debug("No matching search entries after applying filterSummary condition.");
                return null;
            }

            const latestSearchEntry = searchCacheEntries.reduce((latest, current) =>
                (!latest || current.value.expires > latest.value.expires) ? current : latest, null);

            return latestSearchEntry;
        }

    } catch (error) {
        log.error("Error reading or parsing search results from session storage:", error);
        return null;
    }
}


/**
 * Returns the name of the category facet with the highest product count on the current page.
 * Reads from the SearchSpring-rendered category facet list:
 *   #facetedSearch-content--category > ul > li
 * Each <li> contains an <a> with the category name as a text node and the count in a <span>(N)</span>.
 * Returns an empty string when the facet list is absent or empty.
 */
export function getMostRepresentedCategory() {
    const items = document.querySelectorAll('#facetedSearch-content--category > ul > li');
    let bestName = '';
    let bestCount = 0;

    items.forEach(li => {
        const anchor = li.querySelector('a');
        if (!anchor) return;

        // Count: first span whose text matches "(N)"
        let count = 0;
        for (const span of anchor.querySelectorAll('span')) {
            const match = span.textContent.match(/\((\d+)\)/);
            if (match) {
                count = parseInt(match[1], 10);
                break;
            }
        }

        // Name: first text node in the anchor (skips child spans)
        const textNode = Array.from(anchor.childNodes)
            .find(n => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
        const name = textNode?.textContent.trim() ?? '';

        if (count > bestCount && name) {
            bestCount = count;
            bestName = name;
        }
    });

    log.debug('[getMostRepresentedCategory] result:', bestName, '(count:', bestCount, ')');
    return bestName;
}


export function GetSearchResults() {
    // Source 1: SearchSpring session cache — authoritative when SS is active.
    let ssTotal = 0;
    try {
        const rawCacheData = sessionStorage.getItem('ss-networkcache');
        if (rawCacheData) {
            const matchingEntries = Object.entries(JSON.parse(rawCacheData))
                .filter(([key]) => key.startsWith('/api/search/search'));
            if (matchingEntries.length > 0) {
                const latest = matchingEntries.reduce((best, [, entry]) =>
                    !best || entry.expires > best.expires ? entry : best, null);
                ssTotal = latest?.value?.pagination?.totalResults ?? 0;
            }
        }
    } catch(e) {
        log.error('[GetSearchResults] SS cache read error:', e);
    }

    // Source 2: BigCommerce native search total — from LG_PAGE_DATA instead of HBS.
    const bcNativeTotal = window.LG_PAGE_DATA?.pagination?.productResultsTotal || 0;

    // Source 3: DOM fallback — reads SS-rendered count text as a last resort.
    let domTotal = 0;
    const countEl = document.querySelector('.category__title-count');
    if (countEl) {
        const match = countEl.textContent.match(/of\s+([\d,]+)/i);
        if (match) {
            domTotal = parseInt(match[1].replace(/,/g, ''), 10) || 0;
        }
    }

    // Priority: DOM ".category__title-count" → SS cache → BC native.
    // The DOM "of N" reflects the page actually rendered, so it wins over the
    // ss-networkcache total, which survives in-tab navigation and can be stale.
    const total = domTotal > 0 ? domTotal : (ssTotal > 0 ? ssTotal : bcNativeTotal);

    log.debug('[GetSearchResults] SS cache total:', ssTotal, '| BC native total:', bcNativeTotal, '| DOM total:', domTotal, '| final:', total);

    return total;
}
